import { useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Phone, Building, Shield, Users, Truck, Scale, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { GenerateLoadingOrError, cn } from "../../lib/utils";
import { useOrganizationMembers, OrganizationMember } from "@/lib/api/betterAuth";
import { PermissionsEditor } from "./components/PermissionsEditor";
import { CustomerAssignmentEditor } from "./components/CustomerAssignmentEditor";

type SectionKey = "risk" | "freight" | "weight_ticket";

interface SectionConfig {
  key: SectionKey;
  title: string;
  description: string;
  icon: React.ReactNode;
  maxAllowed?: number;
}

const SECTIONS: SectionConfig[] = [
  {
    key: "risk",
    title: "Risk Profile Assignments",
    description: "Assign risk customers to this user.",
    icon: <Users className="h-5 w-5" />,
  },
  {
    key: "freight",
    title: "Freight Customer Assignment",
    description: "Assign freight customer to this user.",
    icon: <Truck className="h-5 w-5" />,
    maxAllowed: 1,
  },
  {
    key: "weight_ticket",
    title: "Weight Ticket Assignment",
    description: "Assign weight ticket customer to this user.",
    icon: <Scale className="h-5 w-5" />,
    maxAllowed: 1,
  },
];

export function BetterAuthPermissionsUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(
    new Set(["risk"])
  );

  // Get organizationId from state (passed from listing page) or default to fetching
  const organizationId = location.state?.organizationId;

  const membersRes = useOrganizationMembers(organizationId);

  const loadingOrErrorUi = GenerateLoadingOrError([membersRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const members = membersRes?.data?.data || [];
  const member = members.find((m: any) => m.id === userId);

  if (!member) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/permissions/mobile")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Mobile Permissions
          </Button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-500">User not found.</p>
        </div>
      </section>
    );
  }

  const handlePermissionsUpdate = () => {
    // Refresh the data
    membersRes.refetch();
  };

  const handleCustomerAssignmentUpdate = () => {
    // Refresh the data
    membersRes.refetch();
  };

  const toggleSection = (key: SectionKey) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getRelationsForType = (member: OrganizationMember, relationType: SectionKey) => {
    if (!member?.relations) return [];
    return member.relations.filter((rel) => rel.relation_type === relationType);
  };

  const getAssignedLabel = (member: OrganizationMember, relationType: SectionKey): string => {
    const relations = getRelationsForType(member, relationType);
    const count = relations.length;
    if (count === 0) return "0 assigned";
    if (count === 1) {
      const name = relations[0].Customer?.long_name;
      if (name) return name;
    }
    return `${count} assigned`;
  };

  // Determine display role
  const displayRole = (member.authuser?.user_id || member.organization?.org_type === "internal") ? "Internal User" : member.role;

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/permissions/mobile")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Mobile Permissions
        </Button>
      </div>

      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Customer Name</p>
                  <p className="text-sm text-gray-600">
                    {member.authuser?.name || "Unknown"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Phone Number</p>
                  <p className="text-sm text-gray-600">
                    {member.authuser?.phoneNumber || "No phone"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-sm text-gray-600 capitalize">
                    {displayRole}
                  </p>
                </div>
              </div>

              {member.organization && (
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">Organization ID</p>
                    <p className="text-sm text-gray-600 font-mono">
                      {member.organization!.name}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Permissions Section - Full width */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permissions Management
            </CardTitle>
            <p className="text-sm text-gray-600">
              Manage user permissions for mobile applications. Changes are saved automatically.
            </p>
          </CardHeader>
          <CardContent>
            <PermissionsEditor
              member={member}
              organizationId={organizationId}
              onSuccess={handlePermissionsUpdate}
              inline={true}
            />
          </CardContent>
        </Card>

        <Separator />

        {/* Customer Assignment Sections - Collapsible */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Assignments
            </CardTitle>
            <p className="text-sm text-gray-600">
              Manage customer assignments for risk, freight, and weight ticket permissions.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {SECTIONS.map((section) => {
              const isOpen = openSections.has(section.key);
              const assignedLabel = getAssignedLabel(member, section.key);

              return (
                <Collapsible
                  key={section.key}
                  open={isOpen}
                  onOpenChange={() => toggleSection(section.key)}
                >
                  <CollapsibleTrigger className="w-full">
                    <div
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border transition-colors",
                        isOpen
                          ? "bg-gray-50 border-gray-300"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="text-gray-600">{section.icon}</span>
                        <div className="text-left">
                          <span className="font-medium">{section.title}</span>
                          <p className="text-sm text-gray-500">{section.description}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {assignedLabel}
                      </span>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 p-4 border rounded-lg bg-white">
                      <CustomerAssignmentEditor
                        member={member}
                        relationType={section.key}
                        onSuccess={handleCustomerAssignmentUpdate}
                        inline={true}
                        maxAllowed={section.maxAllowed}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

export default BetterAuthPermissionsUserDetailPage;
